import { useNavigate } from 'react-router-dom';
import type { Subscription } from './types';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { useT } from '@/shared/lib/i18n';

interface Props {
  sub: Subscription;
  onDelete?: (id: string) => void;
  deleting?: boolean;
}

const CURRENCY_SYMBOL: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€' };

const STEP_LABEL_KEYS: Record<string, string> = {
  price: 'subscription.draft.price',
  members: 'subscription.draft.members',
  collect: 'subscription.draft.collect',
  split: 'subscription.draft.split',
  preview: 'subscription.draft.preview',
};

export function DraftCard({ sub, onDelete, deleting = false }: Props) {
  const navigate = useNavigate();
  const t = useT();
  const sym = CURRENCY_SYMBOL[sub.currency] ?? sub.currency;
  const letter = sub.name.trim()[0]?.toUpperCase() ?? '?';
  const isEmoji = sub.icon.length > 0 && !/^[a-z0-9][a-z0-9-]*$/.test(sub.icon);
  const isSlug = sub.icon.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(sub.icon);
  const openDraft = () => navigate(`/create?draft=${sub.id}`);
  const step = t(STEP_LABEL_KEYS[sub.draft_step] ?? 'subscription.draft.fallback');

  return (
    <div
      role="button"
      tabIndex={0}
      className="w-full text-left rounded-[18px] p-4 transition-all active:scale-[0.98] border-2 border-dashed border-brand-green/40 bg-brand-green/5"
      onClick={openDraft}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDraft();
        }
      }}
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 opacity-70">
          {isEmoji ? (
            <span className="w-[44px] h-[44px] rounded-[12px] bg-brand-gradient/60 flex items-center justify-center text-[24px] leading-none">
              {sub.icon}
            </span>
          ) : isSlug ? (
            <img
              src={`/api/v1/icons/${sub.icon}/default`}
              alt={sub.name}
              className="w-[44px] h-[44px] rounded-[12px] object-contain bg-white/40 border border-white/40 p-1"
            />
          ) : (
            <SubscriptionIcon letter={letter} size="lg" className="opacity-70" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-bold truncate text-text-primary/70">{sub.name}</p>
          <p className="text-[12px] text-brand-green font-semibold mt-0.5">
            {t('subscription.draft.label', { step })}
          </p>
        </div>

        <p className="flex-shrink-0 text-[15px] font-mono tabular-nums text-text-secondary">
          {Number(sub.total_price).toLocaleString('ru')} {sym}
        </p>

        {onDelete && (
          <button
            type="button"
            aria-label={t('subscription.draft.delete')}
            disabled={deleting}
            className="flex-shrink-0 w-9 h-9 rounded-full border border-[#c1262c]/20 bg-[#c1262c]/10 text-[#c1262c] shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] backdrop-blur-[8px] transition active:scale-95 disabled:opacity-50 flex items-center justify-center"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(sub.id);
            }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18" />
              <path d="M8 6V4h8v2" />
              <path d="M6 6l1 15h10l1-15" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
